import React from 'react';
import { Phone, Mail, Users, MessageSquare, MapPin, FileText, CheckCircle2, Circle, Calendar } from 'lucide-react';

const COMM_ICON = {
  call: Phone,
  email: Mail,
  meeting: Users,
  whatsapp: MessageSquare,
  site_visit: MapPin,
  note: FileText,
};

const COMM_COLOR = {
  call: 'bg-emerald-100 text-emerald-700',
  email: 'bg-teal-100 text-teal-700',
  meeting: 'bg-violet-100 text-violet-700',
  whatsapp: 'bg-teal-100 text-teal-700',
  site_visit: 'bg-amber-100 text-amber-700',
  note: 'bg-slate-100 text-slate-700',
};

const fmtDateTime = (s) => {
  if (!s) return '';
  const d = new Date(s);
  return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const fmtDate = (s) => {
  if (!s) return '';
  return new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const isOverdue = (s) => {
  if (!s) return false;
  return new Date(s.slice(0, 10)) < new Date(new Date().toISOString().slice(0, 10));
};

export default function ActivityTimeline({ communications = [], tasks = [], onCompleteTask }) {
  // Merge into a single chronological list
  const items = [
    ...communications.map(c => ({ kind: 'comm', ts: c.created_at, data: c })),
    ...tasks.map(t => ({ kind: 'task', ts: t.reminder_date, data: t })),
  ].sort((a, b) => (b.ts || '').localeCompare(a.ts || ''));

  if (items.length === 0) {
    return (
      <div className="text-center py-12 text-sm text-slate-500" data-testid="activity-timeline-empty">
        No activity yet. Log a note, call, email, meeting or schedule a task to start tracking.
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="activity-timeline">
      {items.map((item, i) => {
        if (item.kind === 'comm') {
          const c = item.data;
          const Icon = COMM_ICON[c.type] || FileText;
          const color = COMM_COLOR[c.type] || 'bg-slate-100 text-slate-700';
          return (
            <div key={`comm-${c.id || i}`} className="border border-slate-200 rounded-sm p-4 hover:border-[#1F7A6E] transition-colors" data-testid={`activity-item-${c.id}`}>
              <div className="flex items-start gap-3">
                <div className={`w-9 h-9 rounded-sm flex items-center justify-center shrink-0 ${color}`}>
                  <Icon size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <div className="flex items-center gap-2">
                      <span className="font-display font-semibold text-sm text-[#0F172A]">{c.subject}</span>
                      <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded-sm">{(c.type || '').replace('_', ' ')}</span>
                      {c.duration_minutes && (
                        <span className="text-[10px] text-slate-500">{c.duration_minutes} min</span>
                      )}
                    </div>
                    <span className="text-[11px] text-slate-500 shrink-0">{fmtDateTime(c.created_at)}</span>
                  </div>
                  {c.notes && <p className="text-sm text-slate-700 whitespace-pre-line">{c.notes}</p>}
                  {c.logged_by && <div className="text-[11px] text-slate-500 mt-1.5">by {c.logged_by}</div>}
                </div>
              </div>
            </div>
          );
        }
        // task
        const t = item.data;
        const overdue = !t.is_completed && isOverdue(t.reminder_date);
        return (
          <div key={`task-${t.id || i}`} className={`border rounded-sm p-4 transition-colors ${overdue ? 'border-red-300 bg-red-50/40' : 'border-slate-200 hover:border-[#1F7A6E]'}`} data-testid={`activity-task-${t.id}`}>
            <div className="flex items-start gap-3">
              <button
                onClick={() => !t.is_completed && onCompleteTask && onCompleteTask(t.id)}
                className={`mt-0.5 shrink-0 ${t.is_completed ? 'text-emerald-600' : 'text-slate-400 hover:text-[#1F7A6E]'}`}
                data-testid={`complete-task-${t.id}`}
                disabled={t.is_completed}
              >
                {t.is_completed ? <CheckCircle2 size={18} /> : <Circle size={18} />}
              </button>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <div className="flex items-center gap-2">
                    <Calendar size={13} className="text-[#1F7A6E]" />
                    <span className={`font-display font-semibold text-sm ${t.is_completed ? 'line-through text-slate-500' : 'text-[#0F172A]'}`}>
                      {t.description}
                    </span>
                    {overdue && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-red-100 text-red-700 rounded-sm">Overdue</span>}
                    {t.is_completed && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded-sm">Done</span>}
                  </div>
                  <span className="text-[11px] text-slate-500 shrink-0">Due {fmtDate(t.reminder_date)}</span>
                </div>
                {t.owner && <div className="text-[11px] text-slate-500">Assigned: {t.owner}</div>}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
